import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { KeyboardArrowDown, Search } from "@mui/icons-material";
import CustomerTable from "../../components/customer/CustomerTable";
import { useNotification } from "../../../../../utils/NotificationsContext";
import DeleteConfirmation from "../../../../../views/components/common/pop-up/DeleteConfirmation";
import { SortType } from "../../../../../models/SortType";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import {
  CustomerActiveStatus,
  CustomerFilters,
} from "../../../../../models/Customer/CustomerFilters";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

// Accepts the current value (active/inactive/all) and legacy URLs (true/false).
const parseActiveStatus = (value: string | null): CustomerActiveStatus => {
  if (value === "all") return "all";
  if (value === "inactive" || value === "false") return "inactive";
  return "active";
};

const CustomerListing: React.FC = () => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { customerStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [activateId, setActivateId] = useState<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialParams: CustomerFilters = {
    page_no: searchParams.get("page_no")
      ? parseInt(searchParams.get("page_no") as string, 10)
      : searchParams.get("page")
        ? parseInt(searchParams.get("page") as string, 10)
        : 1,
    search_text: searchParams.get("search_text") || undefined,
    active_status: parseActiveStatus(
      searchParams.get("active_status") ?? searchParams.get("active")
    ),
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "createdDate",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const [params, setParams] = useState<CustomerFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 400);

  // Sync debounced search to params
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    setParams((prev) => {
      const current = prev.search_text || "";
      if (current === trimmed) return prev;
      return {
        ...prev,
        search_text: trimmed.length > 0 ? trimmed : undefined,
        page_no: 1,
      };
    });
  }, [debouncedSearch]);

  // Fetch customers on params change
  useEffect(() => {
    setIsLoading(true);
    customerStore
      .getCustomers(params)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later."
        )
      )
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Update query params in URL
  useEffect(() => {
    const updateQueryParams = () => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (
          value !== undefined &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          query.set(key, paramValue);
        }
      });

      const queryString = query.toString();
      if (queryString) {
        navigate({ search: queryString }, { replace: true });
      }
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const trimmed = searchTerm.trim();
      setParams((prev) => ({
        ...prev,
        search_text: trimmed.length > 0 ? trimmed : undefined,
        page_no: 1,
      }));
    }
  };

  const handleActiveStatusChange = (value: CustomerActiveStatus) => {
    setParams((prev) => ({
      ...prev,
      active_status: value,
      page_no: 1,
    }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleDeleteConfirm = async (customerId: number) => {
    if (!customerId) return;

    try {
      setIsLoading(true);
      const res = await customerStore.deleteCustomer(customerId);
      setDeleteId(null);

      if (res?.success) {
        showSuccess(res?.result || res?.message || "Customer deleted successfully");
        await customerStore.getCustomers(params);
        return;
      }

      showError("Error Occurred", res?.message || "Error deleting customer");
    } catch (err: any) {
      setDeleteId(null);
      showError(
        "Error Occurred",
        err?.response?.data?.message ||
          "Something went wrong! Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateConfirm = async (customerId: number) => {
    if (!customerId) return;

    try {
      setIsLoading(true);
      const res = await customerStore.activateCustomer(customerId);
      setActivateId(null);

      if (res?.success) {
        showSuccess(res?.message || "Customer activated successfully");
        await customerStore.getCustomers(params);
        return;
      }

      showError("Error Occurred", "Error activating customer");
    } catch (err: any) {
      setActivateId(null);
      showError(
        "Error",
        err?.response?.data?.message ||
          "Something went wrong! Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasCustomerPermission = userStore.hasPermission([
    EmployeeRoles.VIEW_CUSTOMER,
    EmployeeRoles.EDIT_CUSTOMER,
    EmployeeRoles.CREATE_CUSTOMER,
  ]);


  const inputFieldSx = {
    width: { xs: "100%", sm: 280, md: 320 },
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      height: 40,
      backgroundColor: colors.surface,
      fontSize: "0.875rem",
      "& fieldset": {
        borderColor: colors.border,
      },
      "&:hover fieldset": {
        borderColor: colors.accentPurple,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.accentPurple,
        borderWidth: 1.5,
      },
    },
  };

  const selectSx = {
    borderRadius: 2,
    height: 40,
    minWidth: { xs: "100%", sm: 160 },
    backgroundColor: colors.surface,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.label,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.border,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accentPurple,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.accentPurple,
      borderWidth: 1.5,
    },
  };

  return (
    <Grid2 size={{ xs: 12 }}>
      {!hasCustomerPermission && (
        <Paper
          elevation={0}
          sx={{
            mt: "0.6rem",
            p: 4,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            bgcolor: colors.surface,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mb: 2,
            }}
          >
            <CustomTypography variant="heading3" color={colors.title}>
              Customers
            </CustomTypography>
            <CustomTypography variant="body2" color={colors.subtitle}>
              View and manage customers.
            </CustomTypography>
          </Box>
          <PermissionBlockLabel />
        </Paper>
      )}

      {hasCustomerPermission && (
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            minHeight: "100vh",
            bgcolor: colors.pageBg,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <CustomTypography variant="heading3" color={colors.title}>
                Customers
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={colors.subtitle}
                style={{ marginTop: 4 }}
              >
                View and manage customers.
              </CustomTypography>
            </Box>

            {userStore.hasPermission(EmployeeRoles.CREATE_CUSTOMER) && (
              <AppButton
                label="Create Customer"
                variant="primary"
                onClick={() =>
                  navigate("/customers/add-edit-customer", {
                    state: { listingSearch: location.search },
                  })
                }
              />
            )}
          </Box>

          {/* Table Container Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
              overflow: "hidden",
              boxShadow: colors.shadowSoft,
            }}
          >
            {/* Filter Bar */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                p: 2.5,
                gap: 2,
              }}
            >
              <TextField
                size="small"
                placeholder="Search customers by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.subtitle, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputFieldSx}
              />

              <Select
                size="small"
                value={params.active_status ?? "active"}
                onChange={(e) =>
                  handleActiveStatusChange(
                    e.target.value as CustomerActiveStatus
                  )
                }
                IconComponent={KeyboardArrowDown}
                sx={selectSx}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </Box>

            {/* Table / Loading / Empty */}
            {isLoading ? (
              <Box
                sx={{
                  minHeight: "350px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress sx={{ color: colors.accentPurple }} />
              </Box>
            ) : customerStore.customers.length === 0 ? (
              <Box
                sx={{
                  minHeight: "250px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomTypography variant="body1" color={colors.subtitle}>
                  No customers found
                </CustomTypography>
              </Box>
            ) : (
              <CustomerTable
                maxHeight={window.innerHeight - 340}
                customers={customerStore.customers}
                limit={params.limit || customerStore.limit}
                page_no={params.page_no}
                total_count={customerStore.total_count}
                total_pages={customerStore.total_pages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onDelete={(id) => setDeleteId(id)}
                onActivate={(id) => setActivateId(id)}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
                sortBy={params.sort_by}
                sortType={params.sort_type}
              />
            )}
          </Paper>

          {deleteId !== null && (
            <DeleteConfirmation
              id={deleteId}
              open={true}
              onDeleteConfirm={() => handleDeleteConfirm(deleteId)}
              onCancel={() => setDeleteId(null)}
              title="Confirm Deletion"
              content="Are you sure you want to delete this customer?"
            />
          )}

          {activateId !== null && (
            <Dialog
              open={true}
              onClose={() => setActivateId(null)}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  p: 1,
                  maxWidth: 420,
                  width: "100%",
                },
              }}
            >
              <DialogTitle>
                <CustomTypography variant="heading6" color={colors.title}>
                  Confirm Activation
                </CustomTypography>
              </DialogTitle>
              <DialogContent>
                <CustomTypography variant="body1" color={colors.subtitle}>
                  Are you sure you want to make this customer active?
                </CustomTypography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <AppButton
                  label="Cancel"
                  variant="grayscale"
                  onClick={() => setActivateId(null)}
                />
                <AppButton
                  label="Activate"
                  variant="done"
                  onClick={() => handleActivateConfirm(activateId)}
                />
              </DialogActions>
            </Dialog>
          )}
        </Box>
      )}
    </Grid2>
  );
};

export default observer(CustomerListing);
